import { useRef, useState } from 'react';
import type { DropdownProps, MenuItemProps } from 'semantic-ui-react';
import { Dropdown, Menu, Popup, Segment } from 'semantic-ui-react';
import useSWR from 'swr';

import { useAuthContext } from '../../../contexts/AuthContext';
import FHIRFetcher from '../../../data/FHIRFetcher';
import { ACTIVITY_TAB } from '../../../fhir-sdoh/common/constants';
import { type ApiFetchError } from '../../../services/api-client';
import type { Fetched } from '../../../types';
import type { Option } from '../../../types/option';
import type { ResourceEntry } from '../../../utils/bundleNarrowing';
import Tasks from '../../patient/careplan/tasks';
import type { TaskCardHandle } from '../../patient/careplan/tasks';

type ActivityProps = {
  onPatientSelected?: (selectedPatient?: unknown, taskId?: string, patientId?: string) => void;
};

function Activity(props: ActivityProps) {
  const tasksRef = useRef<TaskCardHandle>(null);
  const authInfo = useAuthContext();
  const [activeitem, setActiveItem] = useState<string>(ACTIVITY_TAB.MY_TASKS);
  const [selectedPayer, setSelectedPayer] = useState<string>('All');
  const [newPractitionerData, setNewPractitionerData] = useState<
    ResourceEntry<'Practitioner'>[] | null
  >(null);
  const payersOptions: Option[] = [{ key: 'All', text: 'All', value: 'All' }].concat(authInfo.payers);

  //Activity ans Tasks
  const handleMenuItemClick = (e: React.MouseEvent<HTMLAnchorElement>, menuprops: MenuItemProps) => {
    setActiveItem(menuprops.name as string);
  };

  // Filter all non-CC practitioners that have only 32 digits guid as ID (not email id as GUID)
  // Annotated on the parameter, not via `SWRConfiguration<…>`: SWR 0.5.7's
  // `Configuration` has no `revalidateIfStale`, so typing the whole literal rejects it.
  const practitionerOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
    onSuccess: (data: Fetched<fhir4.Bundle>, _key: unknown, _config: unknown) => {
      const activePractitioners = (data!.entry as ResourceEntry<'Practitioner'>[]).filter(
        (practitioner) => {
          return practitioner.resource.id!.length > 30;
        },
      );
      setNewPractitionerData(activePractitioners);
    },
  };

  const {
    data: _practitionerData,
    error: practitionerError,
    mutate: _practitionerMutate,
  } = useSWR<Fetched<fhir4.Bundle>, ApiFetchError>(
    [
      `/Practitioner?active=true&_count=1000&identifier=https://projectwell.io/fhir/practitioner-types|OA,WA,RD,HC`,
      authInfo.token,
    ],
    FHIRFetcher,
    practitionerOptions
  );

  const handlePayerSelection = (e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => {
    if (selectedPayer !== value) {
      tasksRef.current!.loadData();
      setSelectedPayer(value as string);
    }
  };

  return (
    <Segment style={{ maxHeight: '100vh' }}>
      <Menu pointing>
        <Popup
          flowing
          trigger={
            <Dropdown
              defaultValue={payersOptions[0]!.value}
              pointing
              className="link item"
              style={{ zIndex: '99999' }}
              options={payersOptions}
              onChange={handlePayerSelection}
            />
          }
          content="Payer Organization"
          on="hover"
        />
        <Menu.Item
          key={ACTIVITY_TAB.MY_TASKS}
          name={ACTIVITY_TAB.MY_TASKS}
          active={activeitem === ACTIVITY_TAB.MY_TASKS}
          onClick={handleMenuItemClick}
        />
        <Menu.Item
          key={ACTIVITY_TAB.MY_CARETEAM_TASKS}
          name={ACTIVITY_TAB.MY_CARETEAM_TASKS}
          active={activeitem === ACTIVITY_TAB.MY_CARETEAM_TASKS}
          onClick={handleMenuItemClick}
        />
        <Menu.Item
          key={ACTIVITY_TAB.ALL_TASKS}
          name={ACTIVITY_TAB.ALL_TASKS}
          active={activeitem === ACTIVITY_TAB.ALL_TASKS}
          onClick={handleMenuItemClick}
        />
      </Menu>
      {activeitem === ACTIVITY_TAB.MY_TASKS && (
        <Tasks
          onPatientSelected={props.onPatientSelected}
          activity={true}
          ref={tasksRef}
          selectedPayer={selectedPayer}
          practitionerId={authInfo.practitionerId}
          PractitionerData={newPractitionerData && !practitionerError && newPractitionerData}
          orgId={selectedPayer}
          activeitem={ACTIVITY_TAB.MY_TASKS}
          isCareTeamTasks={false}
        />
      )}
      {activeitem === ACTIVITY_TAB.MY_CARETEAM_TASKS && (
        <Tasks
          onPatientSelected={props.onPatientSelected}
          activity={true}
          ref={tasksRef}
          selectedPayer={selectedPayer}
          practitionerId={authInfo.practitionerId}
          PractitionerData={newPractitionerData && !practitionerError && newPractitionerData}
          orgId={selectedPayer}
          activeitem={ACTIVITY_TAB.MY_CARETEAM_TASKS}
          isCareTeamTasks={true}
        />
      )}
      {activeitem === ACTIVITY_TAB.ALL_TASKS && (
        <Tasks
          onPatientSelected={props.onPatientSelected}
          activity={true}
          ref={tasksRef}
          selectedPayer={selectedPayer}
          practfilter={true}
          PractitionerData={newPractitionerData && !practitionerError && newPractitionerData}
          orgId={selectedPayer}
          activeitem={ACTIVITY_TAB.ALL_TASKS}
          isCareTeamTasks={false}
        />
      )}
    </Segment>
  );
}

export default Activity;
